/**
 * Transportadora REAL escolhida pelo cliente no checkout (site ou CRM), como o
 * FreightHub cotou — distinta do rótulo exibido ("Expresso Aereo - 4 dias úteis",
 * "Frete Grátis - …"), que fica em shipping_chosen_by_customer.
 */
export interface ChosenCarrier {
  chosen_provider_name?: string | null;
  chosen_provider_service_name?: string | null;
  chosen_provider_id?: number | null;
  chosen_provider_service_id?: number | null;
}

/** "MelhorEnvio - LATAM Cargo · éFácil"; null quando a origem não informou. */
export const chosenCarrierLabel = (c: ChosenCarrier): string | null => {
  const provider = c.chosen_provider_name?.trim();
  if (!provider) return null;
  const service = c.chosen_provider_service_name?.trim();
  return service ? `${provider} · ${service}` : provider;
};

/**
 * A cotação corresponde à escolha do cliente? Casa pelos ids resolvidos no
 * ERP; sem id de serviço, basta a transportadora.
 */
export const isQuoteChosenByCustomer = (
  c: ChosenCarrier,
  quote: { provider_id: number; provider_service_id: number },
): boolean => {
  if (!c.chosen_provider_id || c.chosen_provider_id !== quote.provider_id) return false;
  if (!c.chosen_provider_service_id) return true;
  return c.chosen_provider_service_id === quote.provider_service_id;
};

/** O que o card "Frete e Envio" mostra sobre transportadora, sem repetir informação. */
export interface FreightCarrierDisplay {
  /** Escolha do cliente: a transportadora estruturada ou, em pedido antigo, o texto do checkout. */
  chosen: string | null;
  /** true quando `chosen` é só o texto do checkout (pedido anterior à transportadora estruturada). */
  chosenIsLegacyText: boolean;
  /** Opção como apareceu no checkout ("Frete Grátis - 6 dias úteis"), só quando acrescenta algo. */
  checkoutLabel: string | null;
  /** Transportadora que de fato enviou (integração logística), só quando difere da escolha. */
  shippedBy: string | null;
}

const norm = (v?: string | null) => (v ?? '').trim();

/**
 * Decide os campos de transportadora do card "Frete e Envio":
 * - pedido novo: escolha = transportadora/serviço estruturados; o texto do
 *   checkout aparece à parte só se for diferente (ex.: frete promocional);
 * - pedido antigo: escolha = o texto do checkout, marcado como legado;
 * - a transportadora de envio só aparece quando é outra informação.
 */
export const freightCarrierDisplay = (o: ChosenCarrier & {
  shipping_chosen_by_customer?: string | null;
  shipping_modality?: string | null;
}): FreightCarrierDisplay => {
  const structured = chosenCarrierLabel(o);
  const checkout = norm(o.shipping_chosen_by_customer) || null;
  const chosen = structured ?? checkout;
  const shipped = norm(o.shipping_modality) || null;
  return {
    chosen,
    chosenIsLegacyText: !structured && !!checkout,
    checkoutLabel: structured && checkout && checkout !== structured ? checkout : null,
    shippedBy: shipped && shipped !== chosen && shipped !== checkout ? shipped : null,
  };
};
